import { useState, useEffect, useCallback, useRef } from "react";
import { C } from "../../../lib/tokens.js";
import { apiFetch } from "../../../lib/api.js";
import { useToast } from "../../../lib/ToastContext.jsx";
import { useAttachments, downloadAttachment, deleteAttachment, deleteAttachmentErrorMessage } from "./shared.jsx";
import { Spinner } from "../../../components/ui/Misc.jsx";
import { ImageLightbox } from "../../../components/ui/ImageLightbox.jsx";
import { AttachmentItem } from "./AttachmentItem.jsx";

export function AttachmentsTab({ request, onUpdate, onRefresh, me }) {
  const { addToast } = useToast();
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [rawList, setRawList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const fetchSeq = useRef(0);
  // Both listLoading and the initial useAttachments([]) start out false, so on first
  // mount `loading` below is briefly false before fetchList has actually run even once —
  // gate the attachmentCount report on a real fetch having completed, not just !loading,
  // or it reports 0 prematurely and stomps a correct cached count.
  const hasFetchedRef = useRef(false);

  const fetchList = useCallback(
    async (signal) => {
      const seq = ++fetchSeq.current;
      setListLoading(true);
      try {
        const data = await apiFetch(`/attachments?request_id=${encodeURIComponent(request.id)}`, { signal });
        if (fetchSeq.current !== seq) return;
        const list = Array.isArray(data) ? data : (data.data ?? []);
        setRawList(list);
      } catch {
        if (fetchSeq.current === seq) setRawList([]);
      } finally {
        if (fetchSeq.current === seq) {
          setListLoading(false);
          hasFetchedRef.current = true;
        }
      }
    },
    [request.id],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [request.id, request.updatedAt, request.activity?.length, fetchList]);

  const { attachments, loading: blobsLoading } = useAttachments(rawList);
  const loading = listLoading || blobsLoading;

  useEffect(() => {
    // Report from rawList, not the blob-enriched `attachments` — the count shouldn't
    // depend on preview thumbnails having finished loading, and attachments lags rawList
    // by one render right after a fetch resolves (its own separate async blob pass).
    if (hasFetchedRef.current) {
      onUpdate?.(request.id, { attachmentCount: rawList.length });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawList.length]);

  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function uploadFiles(files) {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      await Promise.all(
        list.map((file) => {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("request_id", request.id);
          return apiFetch("/attachments/upload", { method: "POST", body: fd });
        }),
      );
      // onRefresh (fetchDetail) runs first since it also refreshes other fields
      // (e.g. activity); fetchList runs last so the attachmentCount it derives from
      // the attachments list itself (via the effect above) is the count that wins —
      // otherwise the detail refetch's own count could stomp it back to stale.
      await onRefresh?.(request.id);
      await fetchList();
    } catch (err) {
      console.error("Failed to upload attachment", err);
      addToast("Could not upload the attachment.");
    } finally {
      setUploading(false);
    }
  }

  function handleDragOver(e) {
    const hasFiles = Array.from(e.dataTransfer?.types ?? []).includes("Files");
    if (!hasFiles) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingFiles(true);
  }

  function handleDragLeave(e) {
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    setIsDraggingFiles(false);
  }

  function handleDrop(e) {
    const hasFiles = Array.from(e.dataTransfer?.types ?? []).includes("Files");
    if (!hasFiles) return;
    e.preventDefault();
    setIsDraggingFiles(false);
    uploadFiles(e.dataTransfer?.files);
  }

  async function handleDelete(a) {
    try {
      await deleteAttachment(a.id);
      // Same ordering as uploadFiles: refresh detail first, then apply the local
      // list change last so its derived attachmentCount isn't stomped back to stale.
      await onRefresh?.(request.id);
      setRawList((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      addToast(deleteAttachmentErrorMessage(err));
    }
  }

  const items = attachments.map((a) => ({
    ...a,
    onDownload: (onProgress) => downloadAttachment(a, onProgress),
    onDelete: me?.canDeleteAttachments ? () => handleDelete(a) : undefined,
  }));
  const requestItems = items.filter((a) => !a.fromComment);
  const commentItems = items.filter((a) => a.fromComment);

  return (
    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      {loading ? (
        <div
          style={{
            background: C.tin, border: `1px dashed ${C.mercury}`, borderRadius: 6,
            padding: 32, textAlign: "center", color: C.iron, fontSize: 13,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
          }}
        >
          <Spinner size={28} />
          Loading attachments…
        </div>
      ) : items.length === 0 ? (
        <DropZone dragging={isDraggingFiles} />
      ) : (
        <div
          style={{
            background: isDraggingFiles ? C.rowActive : "transparent",
            border: `2px dashed ${isDraggingFiles ? C.liquid : "transparent"}`,
            borderRadius: 10, padding: 12, minHeight: "calc(100vh - 200px)",
            display: "flex", flexDirection: "column", gap: 16,
            transition: "background 0.15s ease, border-color 0.15s ease",
          }}
        >
          <DropZone dragging={isDraggingFiles} compact />
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {requestItems.length > 0 && (
              <div>
                {commentItems.length > 0 && (
                  <div style={{ fontSize: 11, fontWeight: 500, color: C.iron, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
                    From the request
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                  {requestItems.map((a, i) => (
                    <AttachmentItem key={i} a={a} onOpenLightbox={(url, kind) => setLightboxMedia({ url, kind })} />
                  ))}
                </div>
              </div>
            )}
            {commentItems.length > 0 && (
              <div>
                {requestItems.length > 0 && (
                  <div style={{ fontSize: 11, fontWeight: 500, color: C.iron, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8, borderTop: `1px dashed ${C.mercury}`, paddingTop: 16 }}>
                    From comments
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                  {commentItems.map((a, i) => (
                    <AttachmentItem key={i} a={a} onOpenLightbox={(url, kind) => setLightboxMedia({ url, kind })} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {uploading && (
        <div style={{ fontSize: 11, color: C.iron, marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
          <Spinner size={14} />
          Uploading…
        </div>
      )}

      {lightboxMedia && <ImageLightbox url={lightboxMedia.url} kind={lightboxMedia.kind} onClose={() => setLightboxMedia(null)} />}
    </div>
  );
}

function DropZone({ dragging, compact }) {
  if (compact) {
    return (
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          background: dragging ? C.rowActive : C.platinum,
          border: `2px dashed ${dragging ? C.liquid : C.mercury}`,
          borderRadius: 999, padding: "8px 16px 8px 8px",
          transition: "background 0.15s ease, border-color 0.15s ease",
        }}
      >
        <div
          style={{
            width: 26, height: 26, borderRadius: "50%", flex: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: dragging ? C.tin : C.mercury,
            transition: "background 0.15s ease",
          }}
        >
          <span className="material-icons" style={{ fontSize: 15, color: dragging ? C.liquid : C.silver }}>
            {dragging ? "upload_file" : "add_circle_outline"}
          </span>
        </div>
        <span style={{ fontSize: 12, color: dragging ? C.liquidDk : C.iron, fontWeight: dragging ? 600 : 400, flex: 1, minWidth: 0 }}>
          {dragging ? "Drop to attach to this request" : "Drag files here or paste a screenshot to attach."}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        background: dragging ? C.rowActive : C.tin,
        border: `2px dashed ${dragging ? C.liquid : C.mercury}`,
        borderRadius: 10, padding: "44px 24px", textAlign: "center",
        transform: dragging ? "scale(1.01)" : "scale(1)",
        transition: "background 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
      }}
    >
      <div
        style={{
          width: 56, height: 56, margin: "0 auto 14px", borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: dragging ? C.tin : C.platinum,
          transition: "background 0.15s ease",
        }}
      >
        <span className="material-icons" style={{ fontSize: 28, color: dragging ? C.liquid : C.silver }}>
          {dragging ? "upload_file" : "folder_open"}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, color: dragging ? C.liquidDk : C.fg, marginBottom: 4 }}>
        {dragging ? "Drop to attach" : "No attachments yet"}
      </div>
      <div style={{ fontSize: 12, color: C.iron }}>
        Drag files here or paste a screenshot to attach.
      </div>
    </div>
  );
}